import { motion } from 'framer-motion'
import {
  Smile,
  Moon,
  Utensils,
  Pill,
  Activity,
  AlertTriangle,
  Sparkles,
  Play,
  Image as ImageIcon,
  Mic,
  ArrowRight,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Mockups fiéis às telas reais do app (apps/web): Análise Semanal,
 * Alertas Ativos, Evolução e o fluxo "relato → estruturado".
 * Vocabulário e estrutura espelham o produto (tabs, labels, severidades).
 */

const tones = {
  verde: 'bg-green-50 text-green-600',
  teal: 'bg-brand-50 text-brand-600',
  ambar: 'bg-amber-50 text-amber-600',
}

// Moldura de "janela do app"
export function AppFrame({
  label,
  children,
  className,
}: {
  label?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-2xl bg-white border border-surface-200 shadow-xl overflow-hidden transition-transform duration-300 hover:-translate-y-1', className)}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-surface-100 bg-surface-50">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-300" />
        </div>
        {label && <div className="ml-1 text-xs font-medium text-surface-400">{label}</div>}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </div>
  )
}

const PATIENT_TABS = ['Visão Geral', 'Evolução', 'Relatos', 'Análise Semanal', 'Alertas']

/** Análise Semanal — o "resultado pronto" que o profissional recebe. */
export function MockWeeklySummary({ frameless = false }: { frameless?: boolean }) {
  const indicadores: { icon: typeof Smile; label: string; valor: string; tom: keyof typeof tones }[] = [
    { icon: Smile, label: 'Humor', valor: 'Bom na maior parte da semana', tom: 'verde' },
    { icon: Moon, label: 'Sono', valor: 'Melhorou — dormiu mais cedo', tom: 'teal' },
    { icon: Utensils, label: 'Alimentação', valor: 'Sem recusas', tom: 'verde' },
    { icon: Pill, label: 'Medicação', valor: 'Em dia', tom: 'teal' },
    { icon: Activity, label: 'Comportamento', valor: '2 crises, ambas na escola', tom: 'ambar' },
  ]

  const content = (
    <div>
      {/* Header do paciente */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-semibold shrink-0">
          ML
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-surface-900 leading-tight">Miguel, 6 anos</div>
          <div className="text-xs text-surface-500">Semana 12–18 mai · 9 relatos da família</div>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-[11px] font-medium shrink-0">
          <Sparkles className="w-3 h-3" /> Gerada por IA
        </span>
      </div>

      {/* Tabs (fiel ao app) */}
      <div className="flex gap-1 mb-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {PATIENT_TABS.map((t) => (
          <span
            key={t}
            className={cn(
              'px-2.5 py-1 text-[11px] font-medium rounded-md whitespace-nowrap',
              t === 'Análise Semanal' ? 'bg-brand-500 text-white' : 'text-surface-400'
            )}
          >
            {t}
          </span>
        ))}
      </div>

      {/* Indicadores extraídos */}
      <div className="space-y-2">
        {indicadores.map((ind) => (
          <div key={ind.label} className="flex items-center gap-3 p-2.5 rounded-xl bg-surface-50 border border-surface-100">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', tones[ind.tom])}>
              <ind.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-surface-400 font-medium">{ind.label}</div>
              <div className="text-sm text-surface-800 leading-tight">{ind.valor}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Ponto de atenção */}
      <div className="mt-3 flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div>
          <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Ponto de atenção</div>
          <div className="text-sm text-amber-900 leading-tight">Sensibilidade a barulho aumentou esta semana.</div>
        </div>
      </div>

      <div className="mt-3 text-[11px] text-surface-400 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-brand-500" />
        Resumo organizado pela IA — você revisa e decide.
      </div>
    </div>
  )

  if (frameless) return content
  return <AppFrame label="Análise Semanal">{content}</AppFrame>
}

/** A virada: o relato cru (WhatsApp) → contexto estruturado. */
export function MockWhatsToStructured() {
  return (
    <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
      {/* O que chega */}
      <div className="rounded-2xl bg-[#0b141a] p-4 border border-white/5">
        <div className="text-[11px] text-white/40 font-medium mb-3">O que a família manda</div>
        <div className="space-y-2">
          <div className="max-w-[85%] bg-[#1f2c33] text-white/90 text-xs rounded-xl rounded-bl-sm px-3 py-2">
            bom dia! noite agitada, ele acordou 2x 😴 mas tomou café super bem
          </div>
          <div className="max-w-[80%] bg-[#005c4b] text-white/90 text-xs rounded-xl rounded-br-sm px-3 py-2 ml-auto flex items-center gap-2">
            <Play className="w-3.5 h-3.5 shrink-0" />
            <span className="flex-1 h-1 rounded-full bg-white/30 relative">
              <span className="absolute left-0 top-0 h-1 w-1/3 rounded-full bg-white/70" />
            </span>
            <span className="text-[10px] text-white/60">0:47</span>
          </div>
          <div className="max-w-[70%] bg-[#1f2c33] text-white/70 text-xs rounded-xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
            <ImageIcon className="w-3.5 h-3.5" /> foto-almoco.jpg
          </div>
          <div className="max-w-[85%] bg-[#1f2c33] text-white/90 text-xs rounded-xl rounded-bl-sm px-3 py-2">
            na escola teve uma crise quando tocou o sinal
          </div>
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-white/30">
          <Mic className="w-3 h-3" /> texto, áudio e foto — sem padrão
        </div>
      </div>

      {/* Seta / transformação */}
      <div className="flex sm:flex-col items-center justify-center gap-1 text-brand-600">
        <div className="w-10 h-10 rounded-full bg-brand-50 border border-brand-100 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <ArrowRight className="w-5 h-5 sm:rotate-90 text-brand-400" />
        <span className="text-[10px] font-medium text-brand-600 whitespace-nowrap">IA organiza</span>
      </div>

      {/* O que você vê */}
      <div className="rounded-2xl bg-white p-4 border border-surface-200 shadow-sm">
        <div className="text-[11px] text-surface-400 font-medium mb-3">O que você vê</div>
        <div className="space-y-2">
          {[
            { icon: Moon, label: 'Sono', valor: 'Acordou 2x — agitado', tom: 'ambar' as const },
            { icon: Utensils, label: 'Alimentação', valor: 'Café e almoço completos', tom: 'verde' as const },
            { icon: Activity, label: 'Comportamento', valor: 'Crise na escola (sinal/ruído)', tom: 'ambar' as const },
          ].map((ind) => (
            <div key={ind.label} className="flex items-center gap-2.5 p-2 rounded-lg bg-surface-50 border border-surface-100">
              <div className={cn('w-7 h-7 rounded-md flex items-center justify-center shrink-0', tones[ind.tom])}>
                <ind.icon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-surface-400 font-medium">{ind.label}</div>
                <div className="text-xs text-surface-800 leading-tight">{ind.valor}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-brand-600">
          <Check className="w-3 h-3" /> pronto para a próxima sessão
        </div>
      </div>
    </div>
  )
}

/** Alertas Ativos — fiel ao InsightsPanel (texto + paciente + severidade). */
export function MockAlerts() {
  const alerts: { texto: string; paciente: string; sev: 'Média' | 'Alta' | 'Crítica' }[] = [
    { texto: 'Sono irregular há 4 dias seguidos', paciente: 'Miguel, 6 anos', sev: 'Média' },
    { texto: 'Recusa alimentar aumentando', paciente: 'Helena, 5 anos', sev: 'Alta' },
    { texto: 'Crises mais frequentes esta semana', paciente: 'Théo, 8 anos', sev: 'Crítica' },
  ]
  const sevColor: Record<string, string> = {
    'Média': 'bg-yellow-100 text-yellow-800',
    'Alta': 'bg-orange-100 text-orange-800',
    'Crítica': 'bg-red-100 text-red-800',
  }
  return (
    <AppFrame label="Alertas">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-surface-500" />
        <span className="font-semibold text-surface-900 text-sm">Alertas Ativos</span>
      </div>
      <div className="space-y-2">
        {alerts.map((a) => (
          <div key={a.texto} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-surface-100 hover:bg-surface-50 transition-colors">
            <div className="min-w-0">
              <div className="text-sm font-medium text-surface-800 truncate">{a.texto}</div>
              <div className="text-xs text-surface-500">{a.paciente}</div>
            </div>
            <span className={cn('px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0', sevColor[a.sev])}>
              {a.sev}
            </span>
          </div>
        ))}
      </div>
    </AppFrame>
  )
}

/** Evolução — gráfico de linha simples (humor x sono ao longo das semanas). */
export function MockEvolution() {
  const humor = [38, 34, 40, 30, 44, 48, 52, 58]
  const sono = [50, 46, 44, 48, 40, 42, 38, 34]
  const toPath = (vals: number[]) => {
    const w = 280
    const h = 90
    const step = w / (vals.length - 1)
    return vals
      .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(h - (v / 64) * h).toFixed(1)}`)
      .join(' ')
  }
  return (
    <AppFrame label="Evolução">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-surface-900 text-sm">Evolução · últimas 8 semanas</span>
      </div>
      <svg viewBox="0 0 280 100" className="w-full h-auto" role="img" aria-label="Gráfico de evolução de humor e sono">
        {[0, 25, 50, 75].map((y) => (
          <line key={y} x1="0" y1={(y / 100) * 90} x2="280" y2={(y / 100) * 90} stroke="#f0f0f0" strokeWidth="1" />
        ))}
        <motion.path d={toPath(sono)} fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, ease: 'easeOut' }} />
        <motion.path d={toPath(humor)} fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.1, ease: 'easeOut', delay: 0.15 }} />
      </svg>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-surface-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded bg-brand-500" /> Humor</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded bg-amber-500" /> Sono</span>
      </div>
    </AppFrame>
  )
}
